import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'https://fiifit.ro';

export interface CheckInData {
  mood: 'GREAT' | 'GOOD' | 'OKAY' | 'BAD' | 'TERRIBLE';
  emotions: string[];
  intensity: number;
  triggers?: string[];
  notes?: string;
}

export interface CheckIn {
  id: string;
  userId: string;
  mood: string;
  emotions: string[];
  intensity: number;
  triggers: string[];
  notes: string | null;
  recommendations: {
    immediate: Array<{
      type: string;
      title: string;
      duration: string;
      steps?: string[];
      description: string;
      why: string;
    }>;
    daily: Array<{
      type: string;
      title: string;
      description: string;
    }>;
    articles: Array<{
      title: string;
      topic: string;
    }>;
    insights: string;
    urgencyLevel: string;
  } | null;
  aiInsights: string | null;
  urgencyLevel: string | null;
  date: string;
  createdAt: string;
}

export interface WeeklyTrend {
  trend: Array<{
    date: string;
    mood: string;
    intensity: number;
    emotions: string[];
  }>;
  stats: {
    avgIntensity: number;
    dominantMood: string;
    commonEmotions: string[];
    checkInCount: number;
  };
}

export interface Streak {
  currentStreak: number;
  longestStreak: number;
  lastCheckIn: string | null;
}

export interface TherapySession {
  id: string;
  userId: string;
  title: string;
  sessionType: string;
  mainThemes: string[];
  insights: string | null;
  progressNotes: string | null;
  overallSentiment: string | null;
  urgencyLevel: string | null;
  createdAt: string;
  updatedAt: string;
  messages?: TherapyMessage[];
  _count?: {
    messages: number;
  };
}

export interface TherapyMessage {
  id: string;
  sessionId: string;
  role: string;
  content: string;
  sentiment: string | null;
  techniques: string[];
  resourcesShared: string[];
  createdAt: string;
}

export interface TherapyResource {
  id: string;
  type: string;
  title: string;
  description: string;
  content: string;
  categories: string[];
  targetEmotions: string[];
  difficulty: string;
  duration: string | null;
  videoUrl: string | null;
  audioUrl: string | null;
  imageUrl: string | null;
  timesRecommended: number;
  timesUsed: number;
  averageRating: number | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

class TherapyService {
  private getAuthHeader() {
    const token = localStorage.getItem('accessToken');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  // Check-in endpoints
  async createCheckIn(data: CheckInData): Promise<CheckIn> {
    const response = await axios.post(
      `${API_URL}/api/therapy/check-in`,
      data,
      { headers: this.getAuthHeader() }
    );
    return response.data;
  }

  async getTodayCheckIn(): Promise<CheckIn | null> {
    const response = await axios.get(
      `${API_URL}/api/therapy/check-in/today`,
      { headers: this.getAuthHeader() }
    );
    return response.data;
  }

  async getCheckInHistory(days: number = 30): Promise<CheckIn[]> {
    const response = await axios.get(
      `${API_URL}/api/therapy/check-in/history?days=${days}`,
      { headers: this.getAuthHeader() }
    );
    return response.data;
  }

  async getWeeklyTrend(): Promise<WeeklyTrend> {
    const response = await axios.get(
      `${API_URL}/api/therapy/check-in/trend`,
      { headers: this.getAuthHeader() }
    );
    return response.data;
  }

  async getCheckInStreak(): Promise<Streak> {
    const response = await axios.get(
      `${API_URL}/api/therapy/check-in/streak`,
      { headers: this.getAuthHeader() }
    );
    return response.data;
  }

  async deleteCheckIn(checkInId: string): Promise<void> {
    await axios.delete(
      `${API_URL}/api/therapy/check-in/${checkInId}`,
      { headers: this.getAuthHeader() }
    );
  }

  // Therapy chat endpoints
  async getSessions(): Promise<TherapySession[]> {
    const response = await axios.get(
      `${API_URL}/api/therapy/sessions`,
      { headers: this.getAuthHeader() }
    );
    return response.data;
  }

  async createSession(data: { title: string; sessionType?: string }): Promise<TherapySession> {
    const response = await axios.post(
      `${API_URL}/api/therapy/sessions`,
      data,
      { headers: this.getAuthHeader() }
    );
    return response.data;
  }

  async getSession(sessionId: string): Promise<TherapySession> {
    const response = await axios.get(
      `${API_URL}/api/therapy/sessions/${sessionId}`,
      { headers: this.getAuthHeader() }
    );
    return response.data;
  }

  // Streaming message - DEPRECATED: TherapyChatPage now handles streaming directly via fetch+ReadableStream
  // Kept for backward compatibility
  sendMessageStream(
    sessionId: string,
    message: string,
    onChunk: (chunk: string) => void,
    onDone: (messageId: string) => void,
    onError: (error: string) => void
  ): void {
    const token = localStorage.getItem('accessToken');
    
    fetch(`${API_URL}/api/therapy/sessions/${sessionId}/message`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ message })
    }).then(response => {
      // Handle credit errors (402)
      if (response.status === 402) {
        response.json().then(data => {
          onError(data.message || 'Credite insuficiente. Accesează pagina de credite.');
        }).catch(() => {
          onError('Credite insuficiente');
        });
        return;
      }

      if (!response.ok) {
        response.json().then(data => {
          onError(data.error || `Eroare server: ${response.status}`);
        }).catch(() => {
          onError(`Eroare server: ${response.status}`);
        });
        return;
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      
      const readStream = () => {
        reader?.read().then(({ done, value }) => {
          if (done) return;
          
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';
          
          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const data = line.slice(6).trim();
              if (!data) continue;
              try {
                const parsed = JSON.parse(data);
                if (parsed.chunk) {
                  onChunk(parsed.chunk);
                } else if (parsed.done) {
                  onDone(parsed.messageId);
                } else if (parsed.error) {
                  onError(parsed.error);
                }
              } catch (e) {
                // Ignore parse errors
              }
            }
          }
          
          readStream();
        }).catch(error => {
          onError(error.message);
        });
      };
      
      readStream();
    }).catch(error => {
      onError(error.message);
    });
  }

  async deleteSession(sessionId: string): Promise<void> {
    await axios.delete(
      `${API_URL}/api/therapy/sessions/${sessionId}`,
      { headers: this.getAuthHeader() }
    );
  }

  // Resources endpoints
  async getResources(filters?: {
    type?: string;
    category?: string;
    emotion?: string;
  }): Promise<TherapyResource[]> {
    const params = new URLSearchParams();
    if (filters?.type) params.append('type', filters.type);
    if (filters?.category) params.append('category', filters.category);
    if (filters?.emotion) params.append('emotion', filters.emotion);
    
    const response = await axios.get(
      `${API_URL}/api/therapy/resources?${params.toString()}`,
      { headers: this.getAuthHeader() }
    );
    return response.data;
  }

  async submitResourceFeedback(
    resourceId: string,
    feedback: {
      wasHelpful: boolean;
      rating?: number;
      notes?: string;
    }
  ): Promise<void> {
    await axios.post(
      `${API_URL}/api/therapy/resources/${resourceId}/feedback`,
      feedback,
      { headers: this.getAuthHeader() }
    );
  }
}

export const therapyService = new TherapyService();
